// Плавающая кнопка поддержки и окно переписки. Виден только вошедшему:
// диалог привязан к аккаунту, гостю писать не от кого. Окно — не Modal:
// чат не должен запирать приложение, планировать можно с открытой перепиской.
//
// Пока окно открыто, уголок-кнопка спрятан, а само окно прибито к правому
// нижнему углу; «закрыть» здесь значит «свернуть» — переписка никуда не
// девается. Размер окна пользователь тянет за ручку в левом верхнем углу,
// поле ввода растягивается по вертикали само.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useAuth } from '../auth';
import { useI18n, useT } from '../i18n';
import { useRoute } from '../router';
import {
  fetchSupportMessages,
  fetchSupportUnread,
  MAX_SUPPORT_FILE_BYTES,
  MAX_SUPPORT_TEXT,
  sendSupportMessage,
  SUPPORT_FILE_ACCEPT,
} from './api';
import type { SupportMessage } from './api';
import styles from './SupportWidget.module.css';

/** Пока чат открыт — новые сообщения раз в 5 секунд, дешёвым GET ?after=. */
const OPEN_POLL_MS = 5_000;
/** Пока закрыт — только бейдж, раз в минуту (свипер может закрыть диалог). */
const BADGE_POLL_MS = 60_000;

const MIN_W = 280;
const MAX_W = 680;
const MIN_H = 320;

function PlaneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M3 11.3 21 3l-7.7 18-2.6-7.4L3 11.3zm7.7 2.3L21 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SupportWidget() {
  const { user } = useAuth();
  const t = useT();
  const { lang } = useI18n();
  const route = useRoute();

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [closedAt, setClosedAt] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [filesAllowed, setFilesAllowed] = useState(false);
  const [size, setSize] = useState({ w: 340, h: 440 });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const lastIdRef = useRef(0);
  // undefined — closedAt ещё не известен: первый ответ сервера ничего не чистит.
  const closedRef = useRef<number | null | undefined>(undefined);

  const scrollDown = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const merge = useCallback(
    (incoming: SupportMessage[]) => {
      if (incoming.length === 0) return;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const fresh = incoming.filter((m) => !known.has(m.id));
        if (fresh.length === 0) return prev;
        const next = [...prev, ...fresh].sort((a, b) => a.id - b.id);
        lastIdRef.current = next[next.length - 1]?.id ?? lastIdRef.current;
        return next;
      });
      // Прокрутка после рендера свежих сообщений.
      requestAnimationFrame(scrollDown);
    },
    [scrollDown],
  );

  /** Ответ сервера: новое закрытие сессии очищает локальную историю. */
  const applyResponse = useCallback(
    (value: { messages: SupportMessage[]; closedAt: number | null; filesAllowed: boolean }) => {
      const prev = closedRef.current;
      closedRef.current = value.closedAt;
      setClosedAt(value.closedAt);
      setFilesAllowed(value.filesAllowed);
      if (prev !== undefined && value.closedAt !== null && value.closedAt !== prev) {
        setMessages([]);
      }
      merge(value.messages);
    },
    [merge],
  );

  // Бейдж непрочитанных: при входе и затем изредка, пока окно закрыто.
  useEffect(() => {
    if (!user || open) return;
    let cancelled = false;
    const controller = new AbortController();
    const tick = () => {
      fetchSupportUnread(controller.signal).then((res) => {
        if (!cancelled && res.ok) setUnread(res.value.unread);
      });
    };
    tick();
    const timer = setInterval(tick, BADGE_POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, [user, open]);

  // Открытое окно: первый запрос берёт всю переписку, дальше — только новое.
  useEffect(() => {
    if (!user || !open) return;
    let cancelled = false;
    const controller = new AbortController();
    setUnread(0);
    const tick = () => {
      fetchSupportMessages(lastIdRef.current, controller.signal).then((res) => {
        if (cancelled || !res.ok) return;
        applyResponse(res.value);
      });
    };
    tick();
    const timer = setInterval(tick, OPEN_POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, [user, open, applyResponse]);

  // В админке уголок не нужен: поддержка отвечает во вкладке «Чаты», а свой
  // диалог с самим собой администратору ни к чему.
  if (!user || route.path === '/admin') return null;

  async function handleSend() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setError(false);
    const res = await sendSupportMessage(text, lang);
    setSending(false);
    if (res.ok) {
      setDraft('');
      merge([res.value.message]);
    } else {
      setError(true);
    }
  }

  /** Выбранный файл уходит сразу, вместе с текстом из поля, если он есть. */
  function handleFile(input: HTMLInputElement) {
    const picked = input.files?.[0];
    input.value = '';
    if (!picked || sending) return;
    setFileError(false);
    if (picked.size > MAX_SUPPORT_FILE_BYTES || !SUPPORT_FILE_ACCEPT.includes(picked.type)) {
      setFileError(true);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        setSending(true);
        setError(false);
        const res = await sendSupportMessage(draft.trim(), lang, {
          name: picked.name,
          dataUrl: String(reader.result),
        });
        setSending(false);
        if (res.ok) {
          setDraft('');
          merge([res.value.message]);
        } else {
          setError(true);
        }
      })();
    };
    reader.readAsDataURL(picked);
  }

  /** Ручка в левом верхнем углу: тянем — окно растёт влево и вверх. */
  function startResize(e: ReactPointerEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const base = { ...size };
    const maxH = Math.round(window.innerHeight * 0.85);
    const onMove = (ev: PointerEvent) => {
      setSize({
        w: Math.min(MAX_W, Math.max(MIN_W, base.w + (startX - ev.clientX))),
        h: Math.min(maxH, Math.max(MIN_H, base.h + (startY - ev.clientY))),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  const format = (ms: number) =>
    new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(ms);

  return (
    <>
      {open && (
        <section
          className={styles.panel}
          style={{ width: size.w, height: size.h }}
          aria-label={t('support.title')}
        >
          <div
            className={styles.resize}
            role="presentation"
            aria-hidden="true"
            onPointerDown={startResize}
          />
          <header className={styles.head}>
            <span className={styles.title}>{t('support.title')}</span>
            <button
              type="button"
              className={styles.minimize}
              aria-label={t('support.minimize')}
              onClick={() => setOpen(false)}
            >
              —
            </button>
          </header>

          <div ref={listRef} className={styles.list}>
            {messages.length === 0 ? (
              <p className={styles.empty}>
                {closedAt !== null ? t('support.closed') : t('support.empty')}
              </p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={m.author === 'user' ? styles.mine : styles.theirs}>
                  {m.file &&
                    (m.file.mime.startsWith('image/') ? (
                      <img
                        className={styles.image}
                        src={`/api/support/files/${m.id}`}
                        alt={m.file.name}
                      />
                    ) : (
                      <a
                        className={styles.fileLink}
                        href={`/api/support/files/${m.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        📎 {m.file.name}
                      </a>
                    ))}
                  {m.text && <p className={styles.text}>{m.text}</p>}
                  <span className={styles.meta}>
                    {m.author === 'admin' && <span className={styles.who}>{t('support.team')} · </span>}
                    {format(m.createdAt)}
                  </span>
                </div>
              ))
            )}
          </div>

          {error && (
            <p className={styles.error} role="alert">
              {t('support.err')}
            </p>
          )}
          {fileError && (
            <p className={styles.error} role="alert">
              {t('support.attach.bad')}
            </p>
          )}

          <div className={styles.composer}>
            {filesAllowed && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={SUPPORT_FILE_ACCEPT}
                  className="visually-hidden"
                  tabIndex={-1}
                  onChange={(e) => handleFile(e.currentTarget)}
                />
                <button
                  type="button"
                  className={styles.attach}
                  aria-label={t('support.attach')}
                  disabled={sending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  📎
                </button>
              </>
            )}
            <textarea
              className={`textarea ${styles.input}`}
              rows={2}
              value={draft}
              maxLength={MAX_SUPPORT_TEXT}
              placeholder={t('support.placeholder')}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter отправляет, Shift+Enter — новая строка: как в мессенджерах.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <button
              type="button"
              className={styles.send}
              aria-label={t('support.send')}
              disabled={sending || draft.trim() === ''}
              onClick={() => void handleSend()}
            >
              <PlaneIcon />
            </button>
          </div>
        </section>
      )}

      {/* Уголок-треугольник, уложенный в правый нижний угол экрана: знак
          вопроса и очкастый смайлик — «спросить поддержку». Пока окно
          открыто, уголок спрятан: его место занял сам чат. */}
      {!open && (
        <button
          type="button"
          className={styles.fab}
          aria-label={t('support.title')}
          onClick={() => setOpen(true)}
        >
          <span className={styles.wedge} aria-hidden="true" />
          <span className={styles.fabContent} aria-hidden="true">
            <span className={styles.question}>?</span>
            <span className={styles.emoji}>😎</span>
            {/* Счётчик непрочитанных живёт прямо рядом со смайликом; контейнер
                повёрнут вдоль гипотенузы, цифру бейдж доворачивает обратно. */}
            {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
          </span>
        </button>
      )}
    </>
  );
}
